import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from './useDebounce';
import { Playbook } from '../types';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { useTagsApi } from '../apis/tags-apis';

export interface PlaybookFilters {
  tags: string[];
  productNames: string[];
  search: string;
}

export interface PlaybookFilterOptions {
  tags: string[];
  productNames: string[];
}

export interface UsePlaybookFiltersReturn {
  // Data
  playbooks: Playbook[];
  filteredPlaybooks: Playbook[];
  loading: boolean;
  error: string | null;
  
  // Filter state
  filters: PlaybookFilters;
  activeFiltersCount: number;
  
  // Filter options (derived from all playbooks)
  filterOptions: PlaybookFilterOptions;
  
  // Actions
  setTagsFilter: (tags: string[]) => void;
  setProductNamesFilter: (productNames: string[]) => void;
  setSearchFilter: (search: string) => void;
  clearAllFilters: () => void;
  clearFilter: (filterType: keyof PlaybookFilters) => void;
  refreshPlaybooks: () => Promise<void>;
  
  // Utilities
  hasActiveFilters: boolean;
  isFiltering: boolean;
}

export const usePlaybookFilters = (
  playbookType?: 'interactive' | 'one-shot',
  getAllPlaybooks: boolean = false
): UsePlaybookFiltersReturn => {
  const { getPlaybooks } = usePlaybooksApi();
  const { getTags } = useTagsApi();
  
  // Core state
  const [allPlaybooks, setAllPlaybooks] = useState<Playbook[]>([]);
  const [globalTags, setGlobalTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState<PlaybookFilters>({
    tags: [],
    productNames: [],
    search: ''
  });
  
  // Debounced search for performance
  const debouncedSearch = useDebounce(filters.search, 300);
  
  // Memoized filter options using global tags API and extracting product names from playbooks
  const filterOptions = useMemo((): PlaybookFilterOptions => {
    const allProductNames = new Set<string>();
    
    // Extract product names from playbooks
    allPlaybooks.forEach(playbook => {
      if (playbook.playbookProductName && playbook.playbookProductName.trim()) {
        allProductNames.add(playbook.playbookProductName.trim());
      }
    });
    
    return {
      tags: globalTags.sort(), // Use global tags from API instead of scanning playbooks
      productNames: Array.from(allProductNames).sort()
    };
  }, [globalTags, allPlaybooks]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.tags.length > 0 || 
           filters.productNames.length > 0 || 
           filters.search.trim().length > 0;
  }, [filters]);
  
  // Count active filters for UI badges
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.tags.length > 0) count++;
    if (filters.productNames.length > 0) count++;
    if (filters.search.trim().length > 0) count++;
    return count;
  }, [filters]);
  
  // Helper functions to extract complex conditions and reduce cognitive complexity
  const hasMatchingTags = useCallback((playbook: Playbook, filterTags: string[]): boolean => {
    if (filterTags.length === 0) return true;
    
    return filterTags.some(filterTag => 
      playbook.playbookTags?.some(playbookTag => 
        playbookTag.toLowerCase().includes(filterTag.toLowerCase())
      )
    );
  }, []);

  const hasMatchingProduct = useCallback((playbook: Playbook, filterProducts: string[]): boolean => {
    if (filterProducts.length === 0) return true;
    
    return filterProducts.some(filterProduct =>
      playbook.playbookProductName?.toLowerCase().includes(filterProduct.toLowerCase())
    );
  }, []);

  const getSearchFields = useCallback((playbook: Playbook): string[] => {
    return [
      playbook.playbookName,
      playbook.playbookDescription,
      playbook.playbookWorkflow,
      playbook.playbookProductName,
      ...(playbook.playbookTags || [])
    ];
  }, []);

  const hasMatchingSearch = useCallback((playbook: Playbook, searchTerm: string): boolean => {
    if (searchTerm.trim().length === 0) return true;
    
    const normalizedSearchTerm = searchTerm.toLowerCase();
    const searchFields = getSearchFields(playbook);
    
    return searchFields.some(field => 
      field && field.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [getSearchFields]);

  const matchesAllFilters = useCallback((playbook: Playbook): boolean => {
    // Early return for tags filter
    if (!hasMatchingTags(playbook, filters.tags)) {
      return false;
    }
    
    // Early return for product filter
    if (!hasMatchingProduct(playbook, filters.productNames)) {
      return false;
    }
    
    // Early return for search filter
    if (!hasMatchingSearch(playbook, debouncedSearch)) {
      return false;
    }
    
    return true;
  }, [hasMatchingTags, hasMatchingProduct, hasMatchingSearch, filters.tags, filters.productNames, debouncedSearch]);

  // Optimized client-side filtering for immediate UI feedback
  const filteredPlaybooks = useMemo(() => {
    if (!hasActiveFilters) {
      return allPlaybooks;
    }
    
    return allPlaybooks.filter(matchesAllFilters);
  }, [allPlaybooks, hasActiveFilters, matchesAllFilters]);
  
  // Fetch playbooks with backend filtering for optimization
  const fetchPlaybooks = useCallback(async (showLoading: boolean = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      
      // Only use backend filtering for significant filter operations to reduce API calls
      const shouldUseBackendFilter = hasActiveFilters && (
        filters.tags.length > 3 || 
        filters.productNames.length > 3 || 
        debouncedSearch.length > 2
      );
      
      let playbooksData: Playbook[];
      
      if (shouldUseBackendFilter) {
        setIsFiltering(true);
        playbooksData = await getPlaybooks(getAllPlaybooks, playbookType, {
          tags: filters.tags,
          productNames: filters.productNames,
          search: debouncedSearch
        });
      } else {
        // For simple filters, just get all playbooks and filter client-side for better UX
        playbooksData = await getPlaybooks(getAllPlaybooks, playbookType);
      }
      
      setAllPlaybooks(playbooksData);
    } catch (err) {
      console.error('Error fetching playbooks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch playbooks');
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  }, [getPlaybooks, getAllPlaybooks, playbookType, hasActiveFilters, filters.tags, filters.productNames, debouncedSearch]);
  
  // Debounced fetch for search operations
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      // Search value has been debounced, trigger fetch if needed
      if (debouncedSearch.length > 2 || debouncedSearch.length === 0) {
        fetchPlaybooks(false);
      }
    }
  }, [debouncedSearch, fetchPlaybooks, filters.search]);
  
  // Load global tags once on initialization
  useEffect(() => {
    const loadGlobalTags = async () => {
      try {
        const tags = await getTags();
        setGlobalTags(tags.map(tag => tag.tagName));
      } catch (error) {
        console.error('Error loading global tags for filters:', error);
        // Fall back to empty array if global tags fail
        setGlobalTags([]);
      }
    };

    loadGlobalTags();
  }, []); // Only run once on mount

  // Initial load and when filter types change significantly
  useEffect(() => {
    fetchPlaybooks(true);
  }, [playbookType, getAllPlaybooks]); // Only re-fetch on major config changes
  
  // Filter actions
  const setTagsFilter = useCallback((tags: string[]) => {
    setFilters(prev => ({ ...prev, tags }));
  }, []);
  
  const setProductNamesFilter = useCallback((productNames: string[]) => {
    setFilters(prev => ({ ...prev, productNames }));
  }, []);
  
  const setSearchFilter = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
  }, []);
  
  const clearAllFilters = useCallback(() => {
    setFilters({
      tags: [],
      productNames: [],
      search: ''
    });
  }, []);
  
  const clearFilter = useCallback((filterType: keyof PlaybookFilters) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: filterType === 'search' ? '' : []
    }));
  }, []);
  
  const refreshPlaybooks = useCallback(async () => {
    await fetchPlaybooks(true);
  }, [fetchPlaybooks]);
  
  return {
    // Data
    playbooks: allPlaybooks,
    filteredPlaybooks,
    loading,
    error,
    
    // Filter state
    filters,
    activeFiltersCount,
    
    // Filter options
    filterOptions,
    
    // Actions
    setTagsFilter,
    setProductNamesFilter,
    setSearchFilter,
    clearAllFilters,
    clearFilter,
    refreshPlaybooks,
    
    // Utilities
    hasActiveFilters,
    isFiltering
  };
};